import { useState } from 'react'
import { Link } from 'react-router-dom'

import { getManifest } from '@senses/games'

import { Alert, ConfirmDialog, Empty, Spinner } from '../components/ui.tsx'
import { api, type Program } from '../lib/api.ts'
import { useAuth } from '../lib/auth.tsx'
import { useAction, useAsync } from '../lib/hooks.ts'

/** Sdílená knihovna nastavení. Programy vidí celý tým, upravuje je jejich autor. */
export function ProgramsPage(): React.JSX.Element {
  const programs = useAsync(() => api.programs.list(), [])
  const list = programs.data?.programs ?? []

  const byGame = new Map<string, Program[]>()
  for (const program of list) {
    const bucket = byGame.get(program.gameId) ?? []
    bucket.push(program)
    byGame.set(program.gameId, bucket)
  }

  return (
    <main className="page">
      <div className="page__header">
        <div>
          <h1>Programy</h1>
          <p>Uložená nastavení her, která přiřazuješ klientům.</p>
        </div>
        <div className="page__actions">
          <Link to="/programy/novy" className="btn btn--primary">
            Nový program
          </Link>
        </div>
      </div>

      {programs.error && <Alert>{programs.error}</Alert>}
      {programs.loading && <Spinner />}

      {programs.data && list.length === 0 && (
        <Empty title="Zatím žádné programy">
          <p>
            Program je hra s konkrétním nastavením — třeba „MOT, 4 cíle, 20 pokusů".
            Založ první a přiřaď ho klientovi.
          </p>
        </Empty>
      )}

      {[...byGame.entries()].map(([gameId, group]) => {
        const manifest = getManifest(gameId)
        return (
          <section className="section" key={gameId} style={{ marginTop: '1.5rem' }}>
            <div className="section__title">
              <h2>
                {manifest?.icon} {manifest?.name ?? gameId}
              </h2>
              {!manifest && <span className="badge badge--warning">hra není v aplikaci</span>}
            </div>

            <div className="grid grid--wide">
              {group.map((program) => (
                <ProgramCard key={program.id} program={program} onChanged={programs.reload} />
              ))}
            </div>
          </section>
        )
      })}
    </main>
  )
}

function ProgramCard({
  program,
  onChanged,
}: {
  program: Program
  onChanged: () => void
}): React.JSX.Element {
  const { user } = useAuth()
  const [removing, setRemoving] = useState(false)
  const remove = useAction(() => api.programs.remove(program.id))

  const canEdit = user?.role === 'admin' || program.ownerId === user?.id

  return (
    <div className="card">
      <h3>{program.name}</h3>

      {program.description && (
        <p className="muted" style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>
          {program.description}
        </p>
      )}

      <p className="dim" style={{ fontSize: '0.82rem', marginTop: '0.75rem' }}>
        Autor: {program.ownerName ?? 'neznámý'}
      </p>

      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
        <Link to={`/hra/program/${program.id}`} className="btn btn--sm">
          Vyzkoušet
        </Link>
        {canEdit && (
          <Link to={`/programy/${program.id}`} className="btn btn--sm">
            Upravit
          </Link>
        )}
        {canEdit && (
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => setRemoving(true)}
            style={{ marginLeft: 'auto' }}
          >
            Smazat
          </button>
        )}
      </div>

      {removing && (
        <ConfirmDialog
          title="Smazat program"
          message={
            <>
              Opravdu smazat <strong>{program.name}</strong>? Zruší se i všechna jeho přiřazení
              klientům. Odtrénované série zůstanou v historii.
            </>
          }
          confirmLabel="Smazat"
          danger
          pending={remove.pending}
          onCancel={() => setRemoving(false)}
          onConfirm={() => void remove.run().then(onChanged)}
        />
      )}
    </div>
  )
}
